import { useCallback, useEffect, useMemo, useState } from 'react'
import { FieldValues, UseFormReturn } from 'react-hook-form'

import { TabFiltersProps } from '../filter.types'
import { formatArrayParamsWithBrackets, normalizeFiltersToObject } from '@lib/transformFiltersToParams'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { selectFilterMeta, setFilterMetadata } from '@store/modules/filters/filterMetaSlice'

export const useFilterMetadata = <T extends FieldValues, Q>(
  filtersConfig: TabFiltersProps<T, Q>['filtersConfig'],
  form: UseFormReturn<T>,
) => {
  const { loadOptions, filtersArea } = filtersConfig
  const dispatch = useAppDispatch()
  const filterMetaSelector = useMemo(() => selectFilterMeta(filtersArea), [filtersArea])
  const filterData = useAppSelector(filterMetaSelector)
  const [isLoading, setIsLoading] = useState(false)

  const loadFilterMetadata = useCallback(async () => {
    const filters = form.getValues()
    const allParams = normalizeFiltersToObject(filters)
    const transformedParams = formatArrayParamsWithBrackets(allParams) as Q

    setIsLoading(true)

    try {
      const { count, options, bulk_actions } = await loadOptions(transformedParams)
      dispatch(
        setFilterMetadata({
          tab: filtersArea,
          count,
          options,
          bulk_actions,
        }),
      )
    } catch (err) {
      console.error('Failed to load filter metadata', err)
    } finally {
      setIsLoading(false)
    }
  }, [loadOptions, filtersArea, form, dispatch])

  useEffect(() => {
    if (!filterData) {
      loadFilterMetadata()
    }
  }, [loadFilterMetadata, filterData])

  return { filterData, loadFilterMetadata, isLoading }
}
